import Store from 'repatch';
import State from '../core/State';
import CreateCommentInput from '../entity/CreateCommentInput';
import CreatePagePreviewInput from '../entity/CreatePagePreviewInput';
import CreatePositionInput from '../entity/CreatePositionInput';
import UpdateCommentInput from '../entity/UpdateCommentInput';
import ApiErrorHandler from '../service/ApiErrorHandler';
import AppendPreviewPageInput from '../entity/AppendPreviewPageInput';
import PreviewCommentWebApi from '../service/persistent/PreviewCommentWebApi';
import {
  finishFetchingCreatedPreviewId,
  setCreatedPreviewId,
  startFetchingCreatedPreviewId,
} from '../thunk/createdPreviewId';
import { startCreateReturnComment, finishCreateReturnComment } from '../thunk/createReturnComment';
import { startCreateNewPosition, finishCreateNewPosition } from '../thunk/createNewPosition';
import {
  finishFetchingCurrentPagePreview,
  setCurrentPagePreview,
  startFetchingCurrentPagePreview,
} from '../thunk/pagePreview';
import { startUpateComment, finishUpdateComment } from '../thunk/updateComment';
import { finishFetchingPagePreviews, setPagePreviews, startFetchingPagePreviews } from '../thunk/pagePreviews';
import { startAppendPreviewPage, finishAppendPreviewPage } from '../thunk/appendPreviewPage';
import FileReference from '@kaizenplatform/belfast/dist/ApiFileReference';

class PreviewCommentUsecase {
  private previewCommentWebApi: PreviewCommentWebApi;
  private apiErrorHandler: ApiErrorHandler;
  private store: Store<State>;

  constructor({
    previewCommentWebApi,
    apiErrorHandler,
    store,
  }: {
    previewCommentWebApi: PreviewCommentWebApi;
    store: Store<State>;
    apiErrorHandler: ApiErrorHandler;
  }) {
    this.previewCommentWebApi = previewCommentWebApi;
    this.apiErrorHandler = apiErrorHandler;
    this.store = store;
  }

  public getPreviewPages = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingPagePreviews());

    try {
      const pagePreviews = await this.previewCommentWebApi.getPagePreviews(organizationId);

      if (pagePreviews === undefined) {
        this.store.dispatch(finishFetchingPagePreviews());
        return;
      }

      this.store.dispatch(setPagePreviews(pagePreviews));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingPagePreviews());
    }
  };

  public getCurrentPreviewPage = async (shareId: string): Promise<void> => {
    this.store.dispatch(startFetchingCurrentPagePreview());

    try {
      const pagePreview = await this.previewCommentWebApi.getPagePreview(shareId);

      this.store.dispatch(setCurrentPagePreview(pagePreview));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingCurrentPagePreview());
    }
  };

  public createPagePreview = async (
    args:
      | {
          content: string;
          contentOption: 'imageUrl';
          title: string;
          body: string;
          organizationId: string;
        }
      | {
          content: File;
          contentOption: 'upload';
          title: string;
          body: string;
          organizationId: string;
        },
  ): Promise<void> => {
    this.store.dispatch(startFetchingCreatedPreviewId());
    let imageUrl = '';
    if (args.contentOption === 'imageUrl') {
      imageUrl = args.content;
    }
    if (args.contentOption === 'upload') {
      let fileReferece: null | FileReference = null;
      try {
        const result = await this.uploadFile(args.content, args.organizationId);
        fileReferece = result;
      } catch {
        throw new Error('ファイルアップロード中に予期せぬエラーが発生しました。');
      }
      imageUrl = fileReferece?.url ?? '';
    }

    try {
      const pagePreview = await this.previewCommentWebApi.createPagePreview(
        CreatePagePreviewInput.fromJSON({
          title: args.title,
          body: args.body,
          imageUrl,
          organizationId: args.organizationId,
        }),
      );

      this.store.dispatch(setCreatedPreviewId(pagePreview.id));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingCreatedPreviewId());
    }
  };

  public createNewPosition = async (
    positionInput: CreatePositionInput,
    body: string,
    shareId: string,
  ): Promise<void> => {
    this.store.dispatch(startCreateNewPosition());
    try {
      const position = await this.previewCommentWebApi.createPosition(positionInput);
      await this.previewCommentWebApi.createComment(CreateCommentInput.fromJSON({ body, positionId: position.id }));

      await this.getCurrentPreviewPage(shareId);
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishCreateNewPosition());
    }
  };

  public createReturnComment = async (positionId: string, body: string, shareId: string): Promise<void> => {
    this.store.dispatch(startCreateReturnComment());
    try {
      await this.previewCommentWebApi.createComment(CreateCommentInput.fromJSON({ body, positionId: positionId }));

      await this.getCurrentPreviewPage(shareId);
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishCreateReturnComment());
    }
  };

  public uploadFile = async (file: File, organizationId: string) => {
    let fileReferece: null | FileReference = null;
    let bucketId: null | string = null;
    try {
      const organization = await this.previewCommentWebApi.getPreviewCommentOrganization(organizationId);
      if (typeof organization.bucketId !== 'string') {
        throw new Error('bucketIdが取得できませんでした。');
      }
      bucketId = organization.bucketId;
    } catch (error) {
      throw new Error('bucketId取得中に予期せぬエラーが発生しました。');
    }
    try {
      const result = await this.previewCommentWebApi.uploadFile(file, bucketId, organizationId);
      fileReferece = result;
    } catch (error) {
      throw new Error('ファイルアップロード中に予期せぬエラーが発生しました。');
    }
    return fileReferece;
  };

  public appendPreviewPage = async (
    args:
      | {
          pagePreviewId: string;
          content: string;
          contentOption: 'imageUrl';
          shareId: string;
        }
      | {
          pagePreviewId: string;
          content: File;
          contentOption: 'upload';
          shareId: string;
          organizationId: string;
        },
  ): Promise<void> => {
    this.store.dispatch(startAppendPreviewPage());
    let imageUrl = '';
    if (args.contentOption === 'imageUrl') {
      imageUrl = args.content;
    }
    if (args.contentOption === 'upload') {
      let fileReferece: null | FileReference = null;
      try {
        const result = await this.uploadFile(args.content, args.organizationId);
        fileReferece = result;
      } catch {
        throw new Error('ファイルアップロード中に予期せぬエラーが発生しました。');
      }
      imageUrl = fileReferece?.url ?? '';
    }

    try {
      const pagePreviewId = args.pagePreviewId;
      const page = await this.previewCommentWebApi.appendPreviewPage(
        AppendPreviewPageInput.fromJSON({ pagePreviewId, imageUrl }),
      );

      await this.getCurrentPreviewPage(args.shareId);

      console.log(page);
    } catch (error) {
      console.log(error);
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishAppendPreviewPage());
    }
  };

  public updateComment = async (id: string, body: string, shareId: string): Promise<void> => {
    this.store.dispatch(startUpateComment());
    try {
      await this.previewCommentWebApi.updateComment(UpdateCommentInput.fromJSON({ body, id }));

      await this.getCurrentPreviewPage(shareId);
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishUpdateComment());
    }
  };
}

export default PreviewCommentUsecase;
